import { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LogoBackground from '@/components/feature/LogoBackground';

interface MobileLayoutProps {
  children: ReactNode;
}

export default function MobileLayout({ children }: MobileLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { path: '/', icon: 'ri-home-5-line', label: 'Início' },
    { path: '/curso', icon: 'ri-play-circle-line', label: 'Cursos' },
    { path: '/ebook', icon: 'ri-book-open-line', label: 'E-books' },
    { path: '/dashboard', icon: 'ri-user-line', label: 'Perfil' },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#0A0A0F]">
      <LogoBackground />

      {/* Header */}
      <header
        className="sticky top-0 z-50 flex items-center justify-between px-4 h-14 shrink-0"
        style={{
          backdropFilter: 'blur(40px) saturate(280%) brightness(1.04)',
          WebkitBackdropFilter: 'blur(40px) saturate(280%) brightness(1.04)',
          background: 'linear-gradient(to bottom, rgba(10,10,20,0.96) 0%, rgba(10,10,20,0.90) 100%)',
          borderBottom: '1px solid rgba(212,175,55,0.12)',
          boxShadow: '0 2px 20px rgba(0,0,0,0.4)',
        }}
      >
        <img
          src="/images/logotipo.jpg"
          alt="SB Family & Mastery"
          className="h-7 w-auto"
        />
        <button
          onClick={() => navigate('/dashboard')}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-[#B8B8B0] hover:text-[#D4AF37] transition-colors cursor-pointer"
          style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <i className="ri-notification-3-line text-lg"></i>
        </button>
      </header>

      {/* Main content */}
      <main className="relative z-10 flex-1 overflow-y-auto pb-20">
        {children}
      </main>

      {/* Bottom Navigation */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-50"
        style={{
          backdropFilter: 'blur(40px) saturate(280%) brightness(1.04)',
          WebkitBackdropFilter: 'blur(40px) saturate(280%) brightness(1.04)',
          background: 'linear-gradient(to top, rgba(10,10,20,0.98) 0%, rgba(10,10,20,0.92) 100%)',
          borderTop: '1px solid rgba(212,175,55,0.12)',
          boxShadow: '0 -4px 24px rgba(0,0,0,0.45)',
        }}
      >
        <div className="flex items-center justify-around h-16">
          {navItems.map((item) => {
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className="flex flex-col items-center justify-center flex-1 h-full gap-0.5 transition-all duration-200 cursor-pointer relative"
              >
                {active && (
                  <span
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[2px] rounded-b-full"
                    style={{ background: 'linear-gradient(90deg, #D4AF37, #F0D060)' }}
                  />
                )}
                <i
                  className={`${item.icon} text-xl transition-all duration-200 ${
                    active ? 'text-[#D4AF37]' : 'text-[#6B6B6B]'
                  }`}
                ></i>
                <span
                  className={`text-[10px] font-medium transition-all duration-200 ${
                    active ? 'text-[#D4AF37]' : 'text-[#6B6B6B]'
                  }`}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
        {/* Safe area for devices with home bar */}
        <div className="h-safe-area-inset-bottom" style={{ height: 'env(safe-area-inset-bottom, 0px)' }} />
      </nav>
    </div>
  );
}